import { useState, useRef, useEffect } from "react";

interface Message {
  role: "user" | "assistant";
  content: string;
}

const SESSION_ID = "session_" + Math.random().toString(36).substr(2, 9) + "_" + Date.now();
const API_URL = "https://universelle.ma/api/chatbot/message";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      role: "assistant",
      content: "Bonjour ! Je suis l'assistant de l'Agence Universelle. Comment puis-je vous aider dans votre projet d'investissement immobilier au Maroc ?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const suggestions = [
    "Opportunités disponibles",
    "Consultation VIP gratuite",
    "Rentabilité garantie",
    "Investir à Marrakech",
  ];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || loading) return;
    const userMsg: Message = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: SESSION_ID }),
      });
      const data = await res.json();
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply || "Désolé, une erreur est survenue." },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Désolé, le service est temporairement indisponible." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <>
      {/* Launcher button */}
      <div style={{ position: "fixed", bottom: "28px", right: "28px", zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "10px" }}>
        {!open && (
          <div style={{ background: "#1a1a1a", color: "#C9A84C", fontSize: "13px", padding: "8px 14px", borderRadius: "20px", border: "1px solid #C9A84C", whiteSpace: "nowrap" }}>
            Parlez à un conseiller
          </div>
        )}
        <button
          onClick={() => setOpen(!open)}
          style={{ width: "58px", height: "58px", borderRadius: "50%", background: "#C9A84C", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 16px rgba(201,168,76,0.4)" }}
          aria-label="Ouvrir le chat"
        >
          {open ? (
            <svg width="22" height="22" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round"><line x1="4" y1="4" x2="18" y2="18"/><line x1="18" y1="4" x2="4" y2="18"/></svg>
          ) : (
            <svg width="24" height="24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          )}
        </button>
      </div>

      {/* Chat window */}
      {open && (
        <div style={{ position: "fixed", bottom: "100px", right: "28px", zIndex: 9998, width: "360px", maxHeight: "520px", display: "flex", flexDirection: "column", background: "#111010", border: "1px solid #2a2a2a", borderRadius: "16px", overflow: "hidden", fontFamily: "system-ui, sans-serif" }}>

          {/* Header */}
          <div style={{ background: "#1a1a1a", borderBottom: "1px solid #2a2a2a", padding: "14px 16px", display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ width: "38px", height: "38px", borderRadius: "50%", background: "#C9A84C", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="18" height="18" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: "#C9A84C", fontSize: "14px", fontWeight: 600 }}>Assistant Universelle</div>
              <div style={{ color: "#888", fontSize: "11px", display: "flex", alignItems: "center", gap: "5px" }}>
                <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#3bba6e", display: "inline-block" }}></span>
                En ligne
              </div>
            </div>
            <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", color: "#555", cursor: "pointer", fontSize: "20px", lineHeight: 1 }}>×</button>
          </div>

          {/* Messages */}
          <div style={{ flex: 1, overflowY: "auto", padding: "14px", display: "flex", flexDirection: "column", gap: "12px" }}>
            {messages.map((msg, i) => (
              <div key={i} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start", gap: "8px", alignItems: "flex-end" }}>
                {msg.role === "assistant" && (
                  <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "#C9A84C", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <svg width="13" height="13" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5l5-3 5 3v7a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/></svg>
                  </div>
                )}
                <div style={{
                  maxWidth: "80%",
                  padding: "10px 13px",
                  borderRadius: msg.role === "user" ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                  background: msg.role === "user" ? "#C9A84C" : "#1e1e1e",
                  border: msg.role === "assistant" ? "1px solid #2a2a2a" : "none",
                  color: msg.role === "user" ? "#111" : "#ddd",
                  fontSize: "13px",
                  lineHeight: "1.5",
                  fontWeight: msg.role === "user" ? 500 : 400,
                }}>
                  {msg.content}
                </div>
              </div>
            ))}

            {/* Suggestions (only after first bot message) */}
            {messages.length === 1 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: "7px", marginTop: "4px" }}>
                {suggestions.map((s) => (
                  <button key={s} onClick={() => sendMessage(s)} style={{ background: "#1e1e1e", border: "1px solid #C9A84C", color: "#C9A84C", fontSize: "11px", padding: "5px 10px", borderRadius: "20px", cursor: "pointer" }}>
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Typing indicator */}
            {loading && (
              <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: "#C9A84C", flexShrink: 0 }}></div>
                <div style={{ background: "#1e1e1e", border: "1px solid #2a2a2a", borderRadius: "14px 14px 14px 4px", padding: "12px 16px", display: "flex", gap: "5px", alignItems: "center" }}>
                  {[0, 1, 2].map((i) => (
                    <span key={i} style={{ width: "7px", height: "7px", borderRadius: "50%", background: "#C9A84C", display: "inline-block", animation: `pulse 1.2s ${i * 0.2}s infinite`, opacity: 0.4 }}></span>
                  ))}
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{ background: "#1a1a1a", borderTop: "1px solid #2a2a2a", padding: "12px 14px", display: "flex", gap: "10px", alignItems: "center" }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Écrivez votre message…"
              disabled={loading}
              style={{ flex: 1, background: "#111010", border: "1px solid #2a2a2a", borderRadius: "24px", padding: "9px 14px", fontSize: "13px", color: "#ddd", outline: "none" }}
            />
            <button
              onClick={() => sendMessage(input)}
              disabled={loading || !input.trim()}
              style={{ width: "38px", height: "38px", borderRadius: "50%", background: input.trim() ? "#C9A84C" : "#2a2a2a", border: "none", cursor: input.trim() ? "pointer" : "default", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.2s" }}
            >
              <svg width="16" height="16" fill="none" stroke={input.trim() ? "#111" : "#555"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
            </button>
          </div>

          <style>{`@keyframes pulse { 0%,80%,100%{opacity:0.4} 40%{opacity:1} }`}</style>
        </div>
      )}
    </>
  );
}
